import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { FaTags } from 'react-icons/fa';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const LibraryCard = ({ library }) => {
    const navigate = useNavigate();
    const [offers, setOffers] = useState({});

    const fetchOffers = useCallback(async () => {
        try {
            const offersRef = collection(db, 'offers');
            const offersQuery = query(
                offersRef,
                where('libraryId', '==', library.id),
                where('isActive', '==', true)
            );
            const querySnapshot = await getDocs(offersQuery);
            const activeOffers = {};
            querySnapshot.forEach((doc) => {
                const offer = doc.data();
                const now = dayjs();
                const validFrom = dayjs(offer.validFrom?.toDate());
                const validUntil = dayjs(offer.validUntil?.toDate());

                if (now.isSameOrAfter(validFrom) && now.isSameOrBefore(validUntil) && offer.usageCount < offer.maxUsage) {
                    offer.applicableSlots.forEach(slot => {
                        const slotKey = `${slot.time}-${slot.price}`;
                        if (!activeOffers[slotKey]) {
                            activeOffers[slotKey] = [];
                        }
                        activeOffers[slotKey].push(offer);
                    });
                }
            });
            setOffers(activeOffers);
        } catch (error) {
            console.error("Error fetching offers:", error);
        }
    }, [library.id]);

    useEffect(() => {
        if (library.id) {
            fetchOffers();
        }
    }, [library.id, fetchOffers]);

    const calculateDiscountedPrice = (price, activeOffer) => {
        if (!activeOffer) return price;
        const { discountType, discountValue } = activeOffer;
        if (discountType === 'percentage') {
            return (price - (price * discountValue) / 100).toFixed(2);
        }
        return (price - discountValue).toFixed(2);
    };

    const handleClick = () => {
        navigate(`/library/${library.slug || library.id}`);
    };

    return (
        <motion.div
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
            className="bg-white shadow-lg rounded-3xl p-6 m-4 cursor-pointer hover:shadow-2xl transition-shadow duration-300 max-w-5xl mx-auto"
        >
            <div className="flex flex-col md:flex-row items-center">
                <div className="flex-1 mb-6 md:mb-0 md:mr-8">
                    {/* Animated Library Name */}
                    <motion.h3
                        key={library.id} // Ensures animation applies when the library changes
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-4xl font-bold text-gray-800 mb-2"
                    >
                        {library.name}
                    </motion.h3>

                    <p className="text-lg text-gray-600 mb-4">{library.phoneNumber}</p>
                    <div className="text-md mb-4">
                        <p className="font-semibold text-gray-600">
                            <span className="text-gray-400">Pincode:</span> {library.pincode}
                        </p>
                        <p className="font-semibold text-gray-600">
                            <span className="text-gray-400">Total Seats:</span> {library.totalSeats}
                        </p>
                    </div>
                    <div className="text-center mt-6">
                        {/* View Details Button with Click Event */}
                        <motion.button
                            onClick={handleClick}
                            whileHover={{ scale: 1.1 }}
                            className="bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-400 hover:to-teal-500 text-white py-3 px-10 rounded-full text-lg font-semibold shadow-md"
                        >
                            View Details
                        </motion.button>
                    </div>
                </div>
                <div className="flex-1 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 rounded-2xl p-6 text-white shadow-inner">
                    <h4 className="font-semibold mb-4 text-center text-white text-xl">Available Slots</h4>
                    {library.slots?.map((slot, index) => {
                        const slotKey = `${slot.time}-${slot.price}`;
                        const activeOffer = offers[slotKey]?.sort((a, b) => b.discountValue - a.discountValue)[0];
                        const discountedPrice = activeOffer ? calculateDiscountedPrice(slot.price, activeOffer) : null;
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="mb-4 flex flex-col items-start border-b border-yellow-400 pb-3"
                            >
                                <div className="flex justify-between w-full">
                                  <p className="text-lg">{slot.time}</p>
                                  {discountedPrice ? (
                                      <div className="flex flex-col items-end">
                                          <p className="font-bold text-lg text-green-400">₹{discountedPrice}</p>
                                          <p className="text-xs line-through text-gray-400">₹{slot.price}</p>
                                      </div>
                                  ) : (
                                      <p className="font-bold text-lg">₹{slot.price}</p>
                                  )}
                                </div>
                                {activeOffer && (
                                  <div className="flex items-center text-xs mt-1 text-green-400">
                                      <FaTags className="mr-1" />
                                      <span className="truncate">{activeOffer.title}</span>
                                  </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
};

export default LibraryCard;
